import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Tag, Button, Space, Row, Col, Table, Input, message,
    Typography, Progress, Spin, Avatar, Empty, Tabs, Collapse
} from 'antd';
import {
    ArrowLeftOutlined, CheckCircleOutlined,
    ClockCircleOutlined, WalletOutlined,
    CrownOutlined, CarOutlined, TeamOutlined, EyeOutlined,
    TrophyOutlined, PhoneOutlined, DownOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Configuration des paliers avec seuils et montants dynamiques par ville
const getAffiliateMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'account_creation', rides: 0, amount: aff.accountCreation || 450, label: 'Compte' },
        { key: 'activation_rides_5', rides: t.milestoneThreshold1 || 5, amount: aff.rides5 || 900, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', rides: t.milestoneThreshold2 || 15, amount: aff.rides15 || 1350, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', rides: t.milestoneThreshold3 || 35, amount: aff.rides35 || 1800, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', rides: t.milestoneThreshold4 || 55, amount: aff.rides55 || 1800, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const getAmbassadorMilestones = (t = {}, r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', rides: 0, amount: amb.accountCreation || 50, label: 'Compte' },
        { key: 'activation_rides_5', rides: t.milestoneThreshold1 || 5, amount: amb.rides5 || 100, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', rides: t.milestoneThreshold2 || 15, amount: amb.rides15 || 150, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', rides: t.milestoneThreshold3 || 35, amount: amb.rides35 || 200, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', rides: t.milestoneThreshold4 || 55, amount: amb.rides55 || 200, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const BeneficiaryCommissionsPage = () => {
    const { type, beneficiaryId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState('direct');
    const [milestoneThresholds, setMilestoneThresholds] = useState({});
    const [commissionRates, setCommissionRates] = useState({});

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.getCommissionsByBeneficiary(type, beneficiaryId);
            if (response.success) {
                setData(response.data);
                if (response.data.milestoneThresholds) {
                    setMilestoneThresholds(response.data.milestoneThresholds);
                }
                if (response.data.commissionRates) {
                    setCommissionRates(response.data.commissionRates);
                }
            } else {
                message.error('Beneficiaire non trouve');
                navigate('/dashboard/affiliate-commissions');
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            navigate('/dashboard/affiliate-commissions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [type, beneficiaryId]);

    const getStatusConfig = (status) => {
        const configs = {
            pending: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', text: 'En attente', icon: <ClockCircleOutlined /> },
            pending_validation: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', text: 'Validation', icon: <ClockCircleOutlined /> },
            approved: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', text: 'Approuvee', icon: <CheckCircleOutlined /> },
            paid: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', text: 'Payee', icon: <CheckCircleOutlined /> },
            cancelled: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7', text: 'Annulee', icon: <CheckCircleOutlined /> }
        };
        return configs[status] || configs.pending;
    };

    const getMilestones = (t = milestoneThresholds, r = commissionRates) => type === 'ambassador' ? getAmbassadorMilestones(t, r) : getAffiliateMilestones(t, r);
    const getTotalPotential = () => getMilestones().reduce((sum, m) => sum + m.amount, 0);

    const renderMilestoneTag = (achieved, label) => (
        <Tag
            style={{
                margin: '2px',
                borderRadius: '12px',
                border: achieved ? '1px solid #52c41a' : '1px solid #d9d9d9',
                background: achieved ? '#f6ffed' : '#fafafa',
                color: achieved ? '#52c41a' : '#999'
            }}
            icon={achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
        >
            {label}
        </Tag>
    );

    const driverColumns = [
        {
            title: 'Chauffeur',
            key: 'name',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar style={{ background: '#fff7e6', color: '#fa8c16' }} icon={<CarOutlined />} />
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626' }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.phone}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Progression',
            key: 'milestones',
            responsive: ['md'],
            render: (_, record) => {
                const m = record.milestones || {};
                const t = record.milestoneThresholds || milestoneThresholds;
                const t1 = t.milestoneThreshold1 || 5;
                const t2 = t.milestoneThreshold2 || 15;
                const t3 = t.milestoneThreshold3 || 35;
                const t4 = t.milestoneThreshold4 || 55;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {renderMilestoneTag(m.accountCreation, 'Compte')}
                        {renderMilestoneTag(m.rides5, `${t1}`)}
                        {renderMilestoneTag(m.rides15, `${t2}`)}
                        {renderMilestoneTag(m.rides35, `${t3}`)}
                        {renderMilestoneTag(m.rides55, `${t4}`)}
                    </div>
                );
            }
        },
        {
            title: 'Gains',
            key: 'revenue',
            width: 100,
            render: (_, record) => (
                <span style={{ fontWeight: 700, color: '#52c41a', fontSize: 14 }}>
                    {(record.totalRevenue || 0).toLocaleString()} F
                </span>
            )
        }
    ];

    const filterDrivers = (driversList) => {
        if (!searchText) return driversList;
        const search = searchText.toLowerCase();
        return driversList.filter(d =>
            (d.firstName?.toLowerCase() || '').includes(search) ||
            (d.lastName?.toLowerCase() || '').includes(search) ||
            (d.phone || '').includes(search)
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!data) {
        return <Empty description="Beneficiaire non trouve" />;
    }

    const { beneficiary, stats, drivers, directDrivers, affiliatesWithDrivers, summary, commissions } = data;
    const isAmbassador = type === 'ambassador';
    const themeColor = isAmbassador ? '#722ed1' : '#1890ff';
    const milestones = getMilestones();
    const totalPotential = getTotalPotential();

    // Calculer les stats
    const totalDrivers = isAmbassador ? (summary?.directDriversCount || 0) + (summary?.indirectDriversCount || 0) : drivers?.length || 0;
    const totalRevenue = isAmbassador ? (summary?.directRevenue || 0) + (summary?.indirectRevenue || 0) : (drivers || []).reduce((sum, d) => sum + (d.totalRevenue || 0), 0);
    const completedDrivers = (drivers || []).filter(d => d.milestonesCount >= 5).length;
    const progressPercent = totalDrivers > 0 ? Math.round((completedDrivers / totalDrivers) * 100) : 0;

    // Render pour affilie (vue simple)
    const renderAffiliateView = () => (
        <Card
            style={{ borderRadius: 12, height: '100%' }}
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                        Chauffeurs recrutes
                        <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{drivers?.length || 0}</Tag>
                    </span>
                    <Input.Search
                        placeholder="Rechercher..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 220 }}
                        allowClear
                    />
                </div>
            }
            bodyStyle={{ padding: 0 }}
        >
            <Table
                columns={driverColumns}
                dataSource={filterDrivers(drivers || [])}
                rowKey="_id"
                pagination={{ pageSize: 8, showSizeChanger: false, size: 'small', style: { marginRight: 16 } }}
                locale={{ emptyText: <Empty description="Aucun chauffeur recrute" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                size="middle"
            />
        </Card>
    );

    // Render pour ambassadeur (vue avec onglets)
    const renderAmbassadorView = () => (
        <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 0 }}>
            {/* Resume ambassadeur */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', background: '#f9f0ff' }}>
                <Row gutter={16}>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>{summary?.directDriversCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Chauffeurs directs</div>
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{summary?.affiliatesCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Affilies</div>
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{summary?.indirectDriversCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Via affilies</div>
                    </Col>
                </Row>
            </div>

            {/* Recherche */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }}>
                <Input.Search
                    placeholder="Rechercher un chauffeur..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ borderRadius: 8 }}
                    allowClear
                />
            </div>

            {/* Onglets */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                style={{ padding: '0 20px' }}
                items={[
                    {
                        key: 'direct',
                        label: (
                            <span>
                                <CarOutlined style={{ marginRight: 6 }} />
                                Chauffeurs directs
                                <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{directDrivers?.length || 0}</Tag>
                            </span>
                        ),
                        children: (
                            <Table
                                columns={driverColumns}
                                dataSource={filterDrivers(directDrivers || [])}
                                rowKey="_id"
                                pagination={{ pageSize: 6, showSizeChanger: false, size: 'small' }}
                                locale={{ emptyText: <Empty description="Aucun chauffeur recrute directement" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                                size="small"
                            />
                        )
                    },
                    {
                        key: 'affiliates',
                        label: (
                            <span>
                                <TeamOutlined style={{ marginRight: 6 }} />
                                Via affilies
                                <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{summary?.indirectDriversCount || 0}</Tag>
                            </span>
                        ),
                        children: (affiliatesWithDrivers || []).length > 0 ? (
                            <Collapse
                                bordered={false}
                                expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                                style={{ background: 'transparent' }}
                            >
                                {(affiliatesWithDrivers || []).map(affiliate => (
                                    <Panel
                                        key={affiliate._id}
                                        header={
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Avatar size="small" style={{ background: '#1890ff' }} icon={<TeamOutlined />} />
                                                    <div>
                                                        <span style={{ fontWeight: 600 }}>{affiliate.firstName} {affiliate.lastName}</span>
                                                        <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c' }}>{affiliate.phone}</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                    <Tag color="blue">{affiliate.driversCount} chauffeur{affiliate.driversCount > 1 ? 's' : ''}</Tag>
                                                    <span style={{ fontWeight: 700, color: '#52c41a' }}>{(affiliate.totalRevenue || 0).toLocaleString()} F</span>
                                                </div>
                                            </div>
                                        }
                                        style={{ marginBottom: 8, background: '#fafafa', borderRadius: 8 }}
                                    >
                                        <Table
                                            columns={driverColumns}
                                            dataSource={filterDrivers(affiliate.drivers || [])}
                                            rowKey="_id"
                                            pagination={false}
                                            size="small"
                                        />
                                    </Panel>
                                ))}
                            </Collapse>
                        ) : (
                            <Empty description="Aucun affilie avec des chauffeurs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )
                    }
                ]}
            />
        </Card>
    );

    return (
        <div style={{ padding: '16px', maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
                gap: 12
            }}>
                <Space size="middle">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/affiliate-commissions')}
                        style={{ borderRadius: 8 }}
                    />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#262626' }}>
                            Commissions - {beneficiary.firstName} {beneficiary.lastName}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {isAmbassador ? 'Ambassadeur' : 'Affilie'} - {stats?.commissionsCount || 0} commission{(stats?.commissionsCount || 0) > 1 ? 's' : ''}
                        </Text>
                    </div>
                </Space>
                <div style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    background: stats?.pendingCount > 0 ? '#fffbe6' : '#f6ffed',
                    border: `1px solid ${stats?.pendingCount > 0 ? '#ffe58f' : '#b7eb8f'}`,
                    color: stats?.pendingCount > 0 ? '#faad14' : '#52c41a',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    {stats?.pendingCount > 0 ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                    {stats?.pendingCount > 0 ? `${stats.pendingCount} en attente` : 'Toutes payees'}
                </div>
            </div>

            <Row gutter={[20, 20]}>
                {/* Colonne gauche */}
                <Col xs={24} lg={8}>
                    {/* Carte Beneficiaire */}
                    <Card
                        style={{
                            borderRadius: 12,
                            marginBottom: 20,
                            border: `2px solid ${themeColor}20`,
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <div style={{
                            background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
                            padding: 20,
                            textAlign: 'center'
                        }}>
                            <Avatar
                                size={64}
                                style={{ background: themeColor, marginBottom: 12 }}
                                icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                            />
                            <div style={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>
                                {beneficiary.firstName} {beneficiary.lastName}
                            </div>
                            <Tag color={isAmbassador ? 'purple' : 'blue'} style={{ marginTop: 8, borderRadius: 12 }}>
                                {isAmbassador ? 'Ambassadeur' : 'Affilie'}
                            </Tag>
                            <div style={{ marginTop: 12, color: '#8c8c8c', fontSize: 13 }}>
                                <PhoneOutlined style={{ marginRight: 6 }} />
                                {beneficiary.phone || '-'}
                            </div>
                        </div>
                    </Card>

                    {/* Stats Cards */}
                    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <CarOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>{totalDrivers}</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Chauffeurs</div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <TrophyOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                                <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{completedDrivers}</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Completes</div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Revenus */}
                    <Card style={{ borderRadius: 12, marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>Total des commissions</Text>
                            <div style={{ fontSize: 32, fontWeight: 700, color: '#52c41a', margin: '8px 0' }}>
                                {(stats?.totalAmount || 0).toLocaleString()} <span style={{ fontSize: 16 }}>FCFA</span>
                            </div>
                            <Row gutter={8} style={{ marginTop: 12 }}>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#faad14' }}>
                                        En attente
                                        <div style={{ fontWeight: 600 }}>{(stats?.pendingAmount || 0).toLocaleString()} F</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#1890ff' }}>
                                        Approuvees
                                        <div style={{ fontWeight: 600 }}>{(stats?.approvedAmount || 0).toLocaleString()} F</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#52c41a' }}>
                                        Payees
                                        <div style={{ fontWeight: 600 }}>{(stats?.paidAmount || 0).toLocaleString()} F</div>
                                    </div>
                                </Col>
                            </Row>
                            {isAmbassador && summary && (
                                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                                    <div style={{ fontSize: 12, color: '#722ed1' }}>
                                        Direct: {(summary.directRevenue || 0).toLocaleString()} F
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1890ff' }}>
                                        Via affilies: {(summary.indirectRevenue || 0).toLocaleString()} F
                                    </div>
                                </div>
                            )}
                            <Progress
                                percent={progressPercent}
                                strokeColor={{ '0%': '#52c41a', '100%': '#95de64' }}
                                trailColor="#f0f0f0"
                                strokeWidth={8}
                                style={{ marginTop: 12 }}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {completedDrivers}/{totalDrivers} chauffeurs ont atteint tous les paliers
                            </Text>
                        </div>
                    </Card>

                    {/* Paliers */}
                    <Card
                        title={<span style={{ fontWeight: 600 }}>Bareme des commissions</span>}
                        style={{ borderRadius: 12, marginBottom: 20 }}
                        bodyStyle={{ padding: 0 }}
                    >
                        {milestones.map((milestone, index) => (
                            <div
                                key={milestone.key}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '14px 20px',
                                    borderBottom: index < milestones.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    background: index % 2 === 0 ? '#fafafa' : '#fff'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        background: themeColor + '15',
                                        color: themeColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 600,
                                        fontSize: 12
                                    }}>
                                        {index + 1}
                                    </div>
                                    <span style={{ color: '#595959' }}>{milestone.label}</span>
                                </div>
                                <span style={{ fontWeight: 700, color: '#52c41a' }}>
                                    +{milestone.amount.toLocaleString()} F
                                </span>
                            </div>
                        ))}
                        <div style={{
                            padding: '14px 20px',
                            background: themeColor + '10',
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontWeight: 600
                        }}>
                            <span>Total potentiel</span>
                            <span style={{ color: themeColor }}>{totalPotential.toLocaleString()} F</span>
                        </div>
                    </Card>

                    {/* Liste des commissions */}
                    {commissions && commissions.length > 0 && (
                        <Card
                            title={<span style={{ fontWeight: 600 }}>Historique des commissions</span>}
                            style={{ borderRadius: 12 }}
                            bodyStyle={{ padding: 0, maxHeight: 300, overflow: 'auto' }}
                        >
                            {commissions.slice(0, 10).map((commission, index) => {
                                const statusConfig = getStatusConfig(commission.status);
                                return (
                                    <div
                                        key={commission._id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            borderBottom: index < Math.min(commissions.length, 10) - 1 ? '1px solid #f0f0f0' : 'none'
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 500 }}>{commission.type?.replace(/_/g, ' ')}</div>
                                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                {dayjs(commission.createdAt).format('DD MMM YYYY')}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontWeight: 600, color: '#52c41a' }}>
                                                {(commission.amount || 0).toLocaleString()} F
                                            </div>
                                            <Tag
                                                style={{
                                                    borderRadius: 10,
                                                    fontSize: 10,
                                                    border: `1px solid ${statusConfig.border}`,
                                                    background: statusConfig.bg,
                                                    color: statusConfig.color
                                                }}
                                            >
                                                {statusConfig.text}
                                            </Tag>
                                        </div>
                                    </div>
                                );
                            })}
                            {commissions.length > 10 && (
                                <div style={{ padding: '12px 16px', textAlign: 'center', color: '#8c8c8c', fontSize: 12 }}>
                                    Et {commissions.length - 10} autres commissions...
                                </div>
                            )}
                        </Card>
                    )}
                </Col>

                {/* Colonne droite - Liste chauffeurs */}
                <Col xs={24} lg={16}>
                    {isAmbassador ? renderAmbassadorView() : renderAffiliateView()}
                </Col>
            </Row>
        </div>
    );
};

export default BeneficiaryCommissionsPage;
